import { API_HOST } from '@/constants/config';

// SSR-safe tools API client used by `generateStaticParams` and other
// build-time SEO paths (/tools, /tools/[slug], /tools/category/[slug]).
// Mirrors `lib/taskTemplateApi.js` — direct fetch (no auth, no Zustand)
// so it works inside Node during the Expo Router static export.
//
// Runtime/auth-bound calls still live in `lib/api.js` (`toolsApi`,
// `toolCategoriesApi`).

const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const fetchTools = async (path, params = {}) => {
  const url = `${API_HOST}${path}${toQueryString(params)}`;
  let response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Platform': 'web',
      },
    });
  } catch (networkErr) {
    throw new Error('Network error — unable to reach the tools service.');
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      response.ok
        ? 'Tools service returned an unexpected response.'
        : `Tools service returned ${response.status}.`,
    );
  }

  const data = await response.json();
  if (!response.ok || data?.error) {
    throw new Error(data?.message || 'Failed to fetch tools data');
  }

  return data;
};

export const toolApi = {
  // Public list of enabled tools. Used at build-time for
  // `generateStaticParams` on /tools/[slug] and to render the catalog
  // SEO body for /tools and /tools/category/[slug].
  listTools: async ({ category, studio, tag, featured, outputType, limit, page } = {}) => {
    const data = await fetchTools('/json/tools', {
      category, studio, tag, featured, outputType, limit, page,
    });
    return {
      tools: data.tools || [],
      pagination: data.pagination || null,
    };
  },

  // Single tool by slug — public detail with fields, presets, examples
  // and SEO meta.
  getToolBySlug: async (slug) => {
    if (!slug) return null;
    const data = await fetchTools(`/json/tools/${encodeURIComponent(slug)}`);
    return data.tool || null;
  },

  // Public list of enabled tool categories. Used at build-time for
  // `generateStaticParams` on /tools/category/[slug] (so crawlers see
  // every category landing page enumerated).
  listCategories: async () => {
    const data = await fetchTools('/json/tool-categories');
    return {
      categories: data.toolCategories || [],
    };
  },
};
